/* Beyond Bohemian — Origin hero (sections/bb-hero-origin.liquid), 2026-09-13
   Photos: crossfade with a slow zoom-out toward the stone. Captions follow the photo and link to each stone's collection.
   A pause button (WCAG 2.2.2). Resting the mouse on the words, dots, or caption holds the photo. Choosing a photo stops
   the rotation until play is pressed. Phones play through once and settle back on the first photo.
   Light: a band of low sun crosses the italic words after the headline rises (CSS), then again as each photo arrives
   and when a mouse moves onto the headline. Homepage section H2s with italic words fade up and catch the same light as
   they scroll into view. This file also measures the announcement bar so the hero fills the first screen on computers.
   Nothing moves in the theme editor or for visitors whose device asks for reduced motion. */
(() => {
  'use strict';

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
  const motionOn = root.classList.contains('bbm-on') && !reduced && !inEditor && 'IntersectionObserver' in window;
  if (!motionOn) root.classList.remove('bbm-on');

  // Same query as the phone layout in bb-hero-origin.css and the portrait photo's <source> in the section.
  const phone = window.matchMedia('(max-width: 749px), (max-width: 1100px) and (max-aspect-ratio: 4/5)');

  // Match the bbm-glint duration and the hero em's animation-delay in bb-hero-origin.css.
  const SWEEP_MS = 2400;
  const HERO_FIRST_SWEEP_MS = 1300;

  /* Light */
  const sweepUntil = new WeakMap();
  // Send the sweep across one element again, unless it is still crossing.
  function replayGlint(el, delay) {
    if (!motionOn || !el || typeof el.getAnimations !== 'function') return;
    const anim = el.getAnimations().find((a) => a.animationName === 'bbm-glint');
    if (!anim) return;
    const now = performance.now();
    if (now < (sweepUntil.get(el) || 0)) return;
    sweepUntil.set(el, now + delay + SWEEP_MS);
    anim.cancel();
    anim.effect.updateTiming({ delay });
    anim.play();
  }

  // The hero sits under the announcement bar, so its height is the screen minus that bar.
  function measureOffset(hero) {
    const top = Math.round(hero.getBoundingClientRect().top + window.scrollY);
    const offset = Math.min(Math.max(top, 0), 200);
    const current = parseFloat(hero.style.getPropertyValue('--bbh-offset'));
    if (Number.isNaN(current) || Math.abs(current - offset) > 1) hero.style.setProperty('--bbh-offset', `${offset}px`);
  }

  /* Photo series */
  function initHero(hero) {
    if (!hero || hero.dataset.bbhReady) return;
    hero.dataset.bbhReady = '1';
    measureOffset(hero);

    const slides = Array.from(hero.querySelectorAll('[data-bbh-slide]'));
    const segs = Array.from(hero.querySelectorAll('[data-bbh-seg]'));
    const origins = Array.from(hero.querySelectorAll('[data-bbh-origin]'));
    const pauseBtn = hero.querySelector('[data-bbh-pause]');
    const heroEms = Array.from(hero.querySelectorAll('.bbh__h1 em'));
    const DUR = parseInt(hero.dataset.duration, 10) || 6500;

    let idx = 0;
    let timer = null;
    let remaining = DUR;
    let startedAt = performance.now();
    let advances = 0;
    let holding = false;
    let inView = true;
    let userPaused = reduced || inEditor || slides.length < 2;

    // The first pass comes from the CSS; don't restart it while it is still crossing.
    heroEms.forEach((em) => sweepUntil.set(em, HERO_FIRST_SWEEP_MS + SWEEP_MS));
    function sunOnHeadline(delay) {
      heroEms.forEach((em) => replayGlint(em, delay));
    }

    function syncOrigins() {
      origins.forEach((o, k) => {
        const on = k === idx;
        o.classList.toggle('is-on', on);
        if (on) {
          o.removeAttribute('aria-hidden');
          if (o.tagName === 'A') o.removeAttribute('tabindex');
        } else {
          o.setAttribute('aria-hidden', 'true');
          if (o.tagName === 'A') o.setAttribute('tabindex', '-1');
        }
      });
    }
    function canRun() {
      return !userPaused && !holding && inView && !document.hidden && slides.length > 1;
    }
    // remaining always matches the progress bar: set when a photo starts, reduced only while its timer runs.
    function schedule(ms) {
      clearTimeout(timer);
      timer = null;
      remaining = ms;
      if (!canRun()) return;
      startedAt = performance.now();
      timer = setTimeout(() => { timer = null; advance(); }, ms);
    }
    function hold() {
      if (timer === null) return;
      clearTimeout(timer);
      timer = null;
      remaining = Math.max(400, remaining - (performance.now() - startedAt));
    }
    function syncAway() {
      hero.classList.toggle('is-away', !inView || document.hidden);
    }
    function go(i) {
      hero.classList.add('bbh--moved');
      idx = (i + slides.length) % slides.length;
      slides.forEach((s, k) => s.classList.toggle('is-on', k === idx));
      segs.forEach((g, k) => {
        g.classList.remove('is-on');
        g.classList.toggle('is-done', k < idx);
        if (k === idx) g.setAttribute('aria-current', 'true');
        else g.removeAttribute('aria-current');
      });
      if (segs[idx]) {
        void segs[idx].offsetWidth;
        segs[idx].classList.add('is-on');
      }
      syncOrigins();
      // The light crosses the headline as the new photo settles in.
      sunOnHeadline(700);
      schedule(DUR);
    }
    function settle() {
      hold();
      userPaused = true;
      hero.classList.add('is-paused');
      if (pauseBtn) pauseBtn.setAttribute('aria-label', 'Play photos');
    }
    function play() {
      userPaused = false;
      advances = 0;
      hero.classList.remove('is-paused');
      if (pauseBtn) pauseBtn.setAttribute('aria-label', 'Pause photos');
      schedule(remaining);
    }
    function advance() {
      advances += 1;
      go(idx + 1);
      if (phone.matches && advances >= slides.length) settle();
    }

    if (pauseBtn) pauseBtn.addEventListener('click', () => (userPaused ? play() : settle()));
    segs.forEach((g, k) => g.addEventListener('click', () => { go(k); settle(); }));

    [hero.querySelector('.bbh__copy'), hero.querySelector('.bbh__controls'), hero.querySelector('.bbh__origins')]
      .filter(Boolean)
      .forEach((zone) => {
        zone.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || userPaused || holding) return;
          hold();
          holding = true;
          hero.classList.add('is-holding');
        });
        zone.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse' || !holding) return;
          holding = false;
          hero.classList.remove('is-holding');
          schedule(remaining);
        });
      });

    const h1 = hero.querySelector('.bbh__h1');
    if (h1) h1.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') sunOnHeadline(0); });

    // If the first photo arrives after most of the first pass, send the light again so it crosses the photo too.
    const firstImg = slides[0] && slides[0].querySelector('img');
    if (firstImg && !firstImg.complete) {
      firstImg.addEventListener('load', () => {
        if (performance.now() > HERO_FIRST_SWEEP_MS + SWEEP_MS * 0.6) sunOnHeadline(250);
      }, { once: true });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
          syncAway();
          if (!inView) hold();
          else if (timer === null) schedule(remaining);
        });
      }, { threshold: 0.2 }).observe(hero);
    }
    document.addEventListener('visibilitychange', () => {
      syncAway();
      if (document.hidden) hold();
      else if (timer === null) schedule(remaining);
    });

    // Mobile browsers fire resize while the address bar hides, so only re-measure when the width changes.
    let lastWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      requestAnimationFrame(() => measureOffset(hero));
    }, { passive: true });

    hero.addEventListener('shopify:block:select', (e) => {
      const i = slides.indexOf(e.target);
      if (i >= 0) { go(i); settle(); }
    });

    if (userPaused && slides.length > 1) {
      hero.classList.add('is-paused');
      if (pauseBtn) pauseBtn.setAttribute('aria-label', 'Play photos');
    }
    syncOrigins();
    schedule(DUR);
  }

  /* Homepage section headings */
  const glintTimers = new WeakMap();
  function glint(scope, delay) {
    scope.querySelectorAll('.bbm-glint').forEach((g) => {
      clearTimeout(glintTimers.get(g));
      g.classList.remove('is-glinting');
      glintTimers.set(g, setTimeout(() => {
        void g.offsetWidth;
        g.classList.add('is-glinting');
        sweepUntil.set(g, performance.now() + SWEEP_MS);
      }, delay));
    });
  }
  // Warm the light to suit the word: brighter gold on light words over dark sections, deeper amber on dark words.
  function sunColors(em) {
    const color = getComputedStyle(em).color;
    const [r, g, b] = (color.match(/[\d.]+/g) || ['65', '62', '70']).map(Number);
    const light = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 > 0.5;
    em.style.setProperty('--g-base', color);
    em.style.setProperty('--g-mid', light ? '#F2BF78' : '#C0874B');
    em.style.setProperty('--g-hi', light ? '#FFF6E2' : '#EBCB92');
  }
  function initHeadings() {
    if (!motionOn) return;
    const headings = Array.from(document.querySelectorAll('main h2')).filter((h) => (
      !h.closest('[data-bbh]') && (h.querySelector('em') || h.classList.contains('bb-reviews-heading'))
    ));
    if (!headings.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        entry.target.classList.add('is-in');
        glint(entry.target, 700);
      });
    }, { threshold: 0.3, rootMargin: '0px 0px -8% 0px' });

    headings.forEach((h) => {
      const r = h.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) return;
      h.querySelectorAll('em').forEach((em) => {
        sunColors(em);
        em.classList.add('bbm-glint');
      });
      h.setAttribute('data-bbm-h2', '');
      io.observe(h);
      h.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        h.querySelectorAll('.bbm-glint.is-glinting').forEach((g) => replayGlint(g, 0));
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-bbh]').forEach(initHero);
    initHeadings();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-bbh]').forEach(initHero);
  });
})();
